import type { Dispatch, Middleware, MiddlewareAPI, UnknownAction } from '@reduxjs/toolkit'
import { logger } from '@saaga/shared/utils/logger.util.ts'
import { logWebSocketEvent } from '@saaga/shared/utils/websocket-event-logger.util.ts'
import {
  calculateReconnectDelay,
  WS_BASE_RECONNECT_DELAY_MS,
  WS_MAX_RECONNECT_ATTEMPTS,
} from '@saaga/shared/utils/websocket-reconnect.util.ts'
import { handleAppEvent, isAppEvent } from './app-event-handlers.helper'
import { handleEngineEvent, isEngineEvent } from './engine-event-handlers.helper'

interface WebSocketState {
  socket: WebSocket | null
  reconnectAttempts: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  isIntentionalDisconnect: boolean
}

const wsState: WebSocketState = {
  socket: null,
  reconnectAttempts: 0,
  reconnectTimer: null,
  isIntentionalDisconnect: false,
}

export const WS_CONNECT = 'websocket/connect'
export const WS_DISCONNECT = 'websocket/disconnect'
export const WS_SEND = 'websocket/send'

export interface WsConnectPayload {
  url: string
  token?: string
  tenantId?: string
  userId?: string
  plan?: string
}

export interface WsConnectAction {
  type: typeof WS_CONNECT
  payload: WsConnectPayload
  [key: string]: unknown
}

export interface WsDisconnectAction {
  type: typeof WS_DISCONNECT
  [key: string]: unknown
}

export interface WsSendAction {
  type: typeof WS_SEND
  payload: unknown
  [key: string]: unknown
}

type WebSocketActions = WsConnectAction | WsDisconnectAction | WsSendAction
const clearReconnectTimer = (): void => {
  if (!wsState.reconnectTimer) return
  clearTimeout(wsState.reconnectTimer)
  wsState.reconnectTimer = null
}

const closeExistingConnection = (): void => {
  clearReconnectTimer()
  if (!wsState.socket) return
  wsState.socket.onclose = null
  wsState.socket.onerror = null
  wsState.socket.onmessage = null
  wsState.socket.onopen = null
  wsState.socket.close()
  wsState.socket = null
}

const createMessageHandler =
  (store: MiddlewareAPI<Dispatch<UnknownAction>>) =>
  (event: MessageEvent): void => {
    try {
      const data = JSON.parse(event.data as string)
      logWebSocketEvent(data)
      if (isEngineEvent(data)) {
        handleEngineEvent(store, data)
        return
      }
      if (isAppEvent(data)) {
        handleAppEvent(store, data)
      }
    } catch {
      // Silent fail for malformed messages
    }
  }

const createCloseHandler =
  (store: MiddlewareAPI<Dispatch<UnknownAction>>, payload: WsConnectPayload) =>
  (event: CloseEvent): void => {
    logger.warn('[WebSocket] Connection closed', { code: event.code, reason: event.reason })

    if (wsState.isIntentionalDisconnect) return
    if (wsState.reconnectAttempts >= WS_MAX_RECONNECT_ATTEMPTS) {
      logger.warn('[WebSocket] Max reconnect attempts reached, stopping auto-reconnect')
      return
    }

    wsState.reconnectAttempts++
    const delay = calculateReconnectDelay(wsState.reconnectAttempts, WS_BASE_RECONNECT_DELAY_MS)
    logger.info(
      `[WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${wsState.reconnectAttempts}/${WS_MAX_RECONNECT_ATTEMPTS})`
    )

    wsState.reconnectTimer = setTimeout(() => {
      store.dispatch({ type: WS_CONNECT, payload })
    }, delay)
  }

const buildWsUrl = (payload: WsConnectPayload): string => {
  const { url, token, tenantId, userId, plan } = payload
  const params = new URLSearchParams()

  if (token) params.set('token', token)
  if (tenantId) params.set('tenant_id', tenantId)
  if (userId) params.set('user_id', userId)
  if (plan) params.set('x_plan', plan)

  const queryString = params.toString()
  if (!queryString) return url

  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}${queryString}`
}

const handleConnect = (
  store: MiddlewareAPI<Dispatch<UnknownAction>>,
  payload: WsConnectPayload
): void => {
  closeExistingConnection()
  wsState.isIntentionalDisconnect = false

  wsState.socket = new WebSocket(buildWsUrl(payload))

  wsState.socket.onopen = () => {
    logger.info('[WebSocket] Connection established')
    wsState.reconnectAttempts = 0
  }

  wsState.socket.onmessage = createMessageHandler(store)
  wsState.socket.onclose = createCloseHandler(store, payload)
  wsState.socket.onerror = () => {
    logger.warn('[WebSocket] Connection error')
  }
}

const handleDisconnect = (): void => {
  wsState.isIntentionalDisconnect = true
  closeExistingConnection()
  wsState.reconnectAttempts = 0
}

const handleSend = (payload: unknown): void => {
  if (wsState.socket?.readyState !== WebSocket.OPEN) return
  logWebSocketEvent({ direction: 'outgoing', payload })
  wsState.socket.send(JSON.stringify(payload))
}

const actionHandlers: Record<
  string,
  (store: MiddlewareAPI<Dispatch<UnknownAction>>, action: WebSocketActions) => void
> = {
  [WS_CONNECT]: (store, action) => {
    handleConnect(store, (action as WsConnectAction).payload)
  },
  [WS_DISCONNECT]: () => {
    handleDisconnect()
  },
  [WS_SEND]: (_, action) => {
    handleSend((action as WsSendAction).payload)
  },
}

export const createWebSocketMiddleware = (): Middleware => (store) => (next) => (action) => {
  const typedAction = action as WebSocketActions
  const handler = actionHandlers[typedAction.type]

  if (handler) {
    handler(store, typedAction)
  }

  return next(action)
}

export const wsConnect = (payload: WsConnectPayload): WsConnectAction => ({
  type: WS_CONNECT,
  payload,
})

export const wsDisconnect = (): WsDisconnectAction => ({
  type: WS_DISCONNECT,
})

export const wsSend = (data: unknown): WsSendAction => ({
  type: WS_SEND,
  payload: data,
})

export const getWebSocketReadyState = (): number | undefined => wsState.socket?.readyState

export const isWebSocketConnected = (): boolean => wsState.socket?.readyState === WebSocket.OPEN

export const resetReconnectAttempts = (): void => {
  wsState.reconnectAttempts = 0
}

export { setStreamHandlerCallbacks } from './engine-event-handlers.helper'
